import { z } from 'zod';
import { contactStatusSchema } from './contacts.js';
/**
 * Contacts that reach the same mailbox.
 *
 * `contacts` is unique on `(org_id, email)`, so identical addresses cannot
 * exist. What this finds are addresses that differ in a way no mailbox provider
 * honours — a plus tag, or a dot at a provider that ignores them — which is how
 * one person ends up on a list twice after signing up through two forms.
 *
 * Nothing here is decided automatically. Clearing a group deletes contacts, so
 * the server proposes and a person presses.
 */
/** Not exported: every consumer reaches these through a group. */
const duplicateContactSchema = z.object({
    contactId: z.string(),
    email: z.string(),
    name: z.string(),
    company: z.string().nullable(),
    status: contactStatusSchema,
    /** Shown so somebody can see which row is worth keeping. */
    custom: z.record(z.string(), z.string()),
    createdAt: z.iso.datetime(),
});
export const duplicateGroupSchema = z.object({
    /** The mailbox they share, so the grouping explains itself. */
    mailbox: z.string(),
    contacts: z.array(duplicateContactSchema),
});
export const duplicateListSchema = z.object({ groups: z.array(duplicateGroupSchema) });
/**
 * Which groups to clear — by the mailbox they share, never by contact id.
 *
 * The rows behind a mailbox are read by the server in the same transaction
 * that deletes them. A request carrying ids would be acting on the list as it
 * was when the dialog was drawn, which is the one moment it is guaranteed to be
 * out of date by the time it arrives.
 *
 * Absent is every group the server finds, which is the "clear all" button. An
 * empty list is none rather than all: a selection that arrived empty was lost
 * on the way, and deleting contacts because a field was missing is a mistake
 * nothing downstream would catch.
 */
export const clearDuplicatesRequestSchema = z.object({
    mailboxes: z.array(z.string().max(320)).max(500).optional(),
});
/**
 * How many contacts were removed — not how many were asked for.
 *
 * A group somebody else cleared first contributes nothing, and pressing the
 * button twice answers zero rather than failing. That difference is the honest
 * answer to "did that work".
 */
export const clearDuplicatesResponseSchema = z.object({
    removed: z.number().int().min(0),
});
//# sourceMappingURL=duplicates.js.map